import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="w-full px-4 md:px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-50 mb-2">影片未找到</h1>
          <p className="text-foreground-400 mb-6">抱歉，您查找的影片不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-foreground-50 font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${movie.title} - 在线观看 | 91影视看片下载`,
    description: movie.description,
    datePublished: movie.updateDate,
    author: {
      '@type': 'Organization',
      name: '91影视看片下载',
    },
    publisher: {
      '@type': 'Organization',
      name: '91影视看片下载',
    },
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91yingshi.com/' },
      { '@type': 'ListItem', position: 2, name: movie.category, item: `https://www.91yingshi.com/category/${movie.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={movie.bannerImage}
              alt={movie.title}
              className="absolute inset-0 w-full h-full object-cover"
              title={movie.title}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/30 via-background-50/50 to-background-50"></div>
          </div>

          <div className="w-full px-4 md:px-6 -mt-32 relative z-10">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: movie.category, href: `/category/${movie.categorySlug}` },
                  { label: movie.title },
                ]}
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 w-full sm:w-56 lg:w-64">
                <div className="aspect-[2/3] rounded-lg overflow-hidden">
                  <img
                    src={movie.coverImage}
                    alt={movie.title}
                    className="w-full h-full object-cover"
                    title={`${movie.title} - 免费在线观看`}
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-secondary-500/15 text-secondary-400"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-50 mb-3">
                  {movie.title}
                </h1>
                <p className="text-foreground-400 text-sm mb-4">{movie.titleEn}</p>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="flex items-center gap-1 text-accent-400 font-semibold text-lg">
                    <i className="ri-star-fill"></i>
                    {movie.rating}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-foreground-300 text-sm">{movie.year}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-foreground-300 text-sm">{movie.duration}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-foreground-300 text-sm">{movie.region}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="px-2 py-0.5 text-xs rounded bg-accent-500/20 text-accent-400 font-medium">
                    {movie.quality}
                  </span>
                </div>

                <div className="flex flex-wrap gap-3 mb-8">
                  <button className="px-8 py-3 rounded-full bg-primary-500 text-foreground-50 font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-fill mr-2"></i>
                    立即播放
                  </button>
                  <button className="px-6 py-3 rounded-full bg-background-100 text-foreground-200 font-medium hover:bg-background-200/70 transition-colors duration-200 whitespace-nowrap cursor-pointer border border-background-200/50">
                    <i className="ri-download-line mr-2"></i>
                    下载观看
                  </button>
                  <button className="w-11 h-11 flex items-center justify-center rounded-full bg-background-100 text-foreground-300 hover:text-primary-400 transition-colors duration-200 cursor-pointer border border-background-200/50">
                    <i className="ri-heart-line"></i>
                  </button>
                  <button className="w-11 h-11 flex items-center justify-center rounded-full bg-background-100 text-foreground-300 hover:text-primary-400 transition-colors duration-200 cursor-pointer border border-background-200/50">
                    <i className="ri-share-forward-line"></i>
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 p-5 rounded-lg bg-background-100 border border-background-200/50">
                  <div>
                    <span className="text-foreground-500 text-xs">导演</span>
                    <p className="text-foreground-200 text-sm font-medium mt-1">{movie.director}</p>
                  </div>
                  <div>
                    <span className="text-foreground-500 text-xs">主演</span>
                    <p className="text-foreground-200 text-sm font-medium mt-1">
                      {movie.cast.slice(0, 3).join('、')}
                      {movie.cast.length > 3 ? ' 等' : ''}
                    </p>
                  </div>
                  <div>
                    <span className="text-foreground-500 text-xs">集数</span>
                    <p className="text-foreground-200 text-sm font-medium mt-1">{movie.episodes}</p>
                  </div>
                </div>

                <div className="mb-10">
                  <h2 className="font-heading text-lg font-semibold text-foreground-100 mb-3">影片简介</h2>
                  <p className="text-foreground-300 text-sm leading-relaxed">
                    {movie.longDescription}
                  </p>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mb-14">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-foreground-50">
                    同类推荐
                  </h2>
                  <Link
                    to={`/category/${movie.categorySlug}`}
                    className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors duration-200 whitespace-nowrap"
                  >
                    查看更多{movie.category}
                    <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}